import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { NEUTRAL_COLOR } from '../models/annotation';

@Injectable({ providedIn: 'root' })
export class CommentsService {
    /**
     * Structure of comments map
     * Outer map will map plotId to a plots comments.
     * The inner BehaviourSubject will contain a Map that contains each comments
     * color as a 'key' and each comments message as a 'value'
     * This way we guarantee only 1 comment per color and make it easy to look up
     * a commment by plotId and color
     */
    plotCommentsMap: Map<string, BehaviorSubject<Map<string, string>>> = new Map();

    /** a new comment map contains only a grey box which is for general comments */
    newCommentsMap( plotId: string ) {
        const newMap = new Map();
        newMap.set( 'a', '' );
        return this.plotCommentsMap.set( plotId, new BehaviorSubject( newMap ));
    }

    /** returns an observable that emits when a plot's comments are updated */
    getPlotComments$( plotId: string ) {
        if ( this.plotCommentsMap.has( plotId  ) ) {
            return this.plotCommentsMap.get( plotId ).pipe( debounceTime( 50 ) );
        } else {
            return this.newCommentsMap( plotId ).get( plotId ).pipe( debounceTime( 50 ) );
        }
    }

    /** replace the existing comments on a plot with a new set */
    setPlotComments( plotId: string, commentMap: Map<string, string> ) {
        const commentsObservable = this.plotCommentsMap.get( plotId );
        commentsObservable.next( commentMap );
    }

    /** adds a new color to a plot's comment map. The color will be the new key */
    addNewColor( plotId: string, color: string ) {
        const commentMap = this.plotCommentsMap.get( plotId ).getValue();
        if ( !commentMap.has( color ) ) {
            commentMap.set( color, '' );
            this.plotCommentsMap.get( plotId ).next( commentMap );
        }
    }

    /** overwrite the existing comment for a given plot and color */
    setPlotComment( plotId: string, color: string, message: string ) {
        const commentsObservable = this.plotCommentsMap.get( plotId );
        commentsObservable.next( commentsObservable.getValue().set( color, message ) );
    }

    /** delete a comment color from a plot's comments */
    removePlotComment( plotId: string, color: string ) {
        const commentsObservable = this.plotCommentsMap.get( plotId );
        const commentMap = commentsObservable.getValue();
        // don't allow removal of the nuetral plot comment
        if ( color !== NEUTRAL_COLOR.color ) {
            commentMap.delete( color );
            this.setPlotComments( plotId, commentMap );
        }
    }
}
